import React, { useEffect, useState } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { Input } from "../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../shadcn/components/ui/button";
import { Dropdown } from "../../../../../shadcn/components/custom/Dropdown";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger,
  DialogDescription,
} from "../../../../../shadcn/components/ui/dialog";
import CalendarDotsIcon from "../../../../../icons/CalendarDotsIcon";
import { Calendar } from "../../../../../shadcn/components/ui/calendar";
// import FileUploader from "../FileUploader";
import CustomMultipleFileUploader from "../../../../banker/bidder/CustomMultipleFileUploader";
import {
  getBidderByUuid,
  getMultipleFilesByOwnerIdentifier,
  updateBidderForEvent,
} from "services";
import { Label } from "../../../../../shadcn/components/ui/label";
import ExclamationIcon from "../../../../../icons/ExclamationIcon";

interface NIBiddingApplicationFormProps {
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  bidderUuid: any;
  init: any;
  bidder: any;
  outerNextStep?: string | null;
  hideHeader?: boolean;
}
export default function NIBiddingApplicationForm({
  bidderUuid,
  init,
  stepsTitles,
  activeStep,
  setActiveStep,
  bidder,
  outerNextStep = null,
  hideHeader = false,
}: NIBiddingApplicationFormProps) {
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const [loading, setLoading] = useState(false);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);

  const form = useBetaForm({
    email: "",
    mobile: "",
    pan: "",
    name: "",
    event_id: "",
    emd_amount: "",
    transaction_amount: "",
    bid_amount: "",
    transaction_id: "",
    emd_proof_uuid: "",
    emd_paid_date: "",
    emd_paid_from: "",
    relationship: "",
    payment_mode: "",
  });
  const form_constraints = {
    mobile: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    email: {
      presence: {
        allowEmpty: false,
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
      },
    },
    name: {
      presence: {
        allowEmpty: true,
      },
    },
    event_id: {
      presence: { allowEmpty: false },
    },
    emd_amount: {
      presence: { allowEmpty: false },
      numericality: {
        greaterThan: 0,
        message: "^EMD amount must be a positive number",
      },
    },
    transaction_amount: {
      presence: { allowEmpty: false },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo:
          typeof form?.getField("emd_amount") === "string"
            ? parseInt(form?.getField("emd_amount"))
            : form?.getField("emd_amount"),
        message:
          "^Transaction amount must be greater than or equal to EMD amount",
      },
    },
    bid_amount: {
      presence: {
        allowEmpty: false,
      },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo:
          typeof bidder?.meta?.reserve_price === "string"
            ? parseInt(bidder?.meta?.reserve_price)
            : bidder?.meta?.reserve_price,
        message: `^Bid amount must be greater than or equal to reserve price:${bidder?.meta?.reserve_price}`,
      },
    },
    transaction_id: {
      presence: { allowEmpty: false },
    },
    emd_proof_uuid: {
      presence: { allowEmpty: false },
    },
    emd_paid_date: {
      presence: { allowEmpty: false },
    },
    emd_paid_from: {
      presence: { allowEmpty: false },
    },
    // relationship: {
    //   presence: { allowEmpty: false },
    // },
    payment_mode: {
      presence: { allowEmpty: false },
    },
  };
  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form?.value]);

  const types = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];
  const emdPaymentMode = [
    { label: "Online Transfer", value: "online" },
    { label: "DD/PO Submitted", value: "dd" },
  ];

  useEffect(() => {
    const loadEMDFiles = async () => {
      if (bidderUuid) {
        try {
          const emdFilesData: any = await getMultipleFilesByOwnerIdentifier(
            bidderUuid,
            "emd-payment-slip"
          );

          console.log("emdFilesData: ", emdFilesData);
          if (emdFilesData && emdFilesData.length > 0) {
            const formattedFiles = emdFilesData.map((item: any) => ({
              id: item?.uuid,
              filename: item?.file_name || item?.filename || "",
              mime: item?.mime || "",
            }));
            console.log("formattedFiles: ", formattedFiles);
            setKycFiles(formattedFiles);
            setRenderKey((prev) => prev + 1);
          }
        } catch (error) {
          console.log("Error loading EMD files:", error);
        }
      }
    };

    const applicationFormData = bidder?.application_form_data?.form_data;
    const paymentData = bidder?.payment_data;

    if (applicationFormData) {
      form.setField("email", applicationFormData?.email || "");
      form.setField("mobile", applicationFormData?.mobile || "");
      form.setField("pan", applicationFormData?.pan || "");
      form.setField("name", applicationFormData?.name || "");
      form.setField("event_id", applicationFormData?.event_id || "");
      form.setField("emd_amount", applicationFormData?.emd_amount || "");
      form.setField(
        "transaction_amount",
        applicationFormData?.transaction_amount || ""
      );
      form.setField("bid_amount", applicationFormData?.bid_amount || "");
      form.setField(
        "transaction_id",
        applicationFormData?.transaction_id || ""
      );
      form.setField("emd_paid_from", applicationFormData?.emd_paid_from || "");
      form.setField("relationship", applicationFormData?.relationship || "");
      form.setField("payment_mode", applicationFormData?.payment_mode || "");

      // Load EMD paid date from payment_data or form_data
      const emdPaidDate =
        paymentData?.emd_paid_date || applicationFormData?.emd_paid_date;
      if (emdPaidDate) {
        // If it's a string, convert to Date object
        const dateValue =
          typeof emdPaidDate === "string" ? new Date(emdPaidDate) : emdPaidDate;
        if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
          form.setField("emd_paid_date", dateValue);
        }
      }

      // Load EMD files from API
      (async () => {
        await loadEMDFiles();
      })();
    } else {
      form.setField("email", bidder?.email || "");
      form.setField("mobile", bidder?.meta?.user_details?.mobile_number || "");
      form.setField("pan", onboardingData?.pan || "");
      form.setField("name", onboardingData?.name || "");
      form.setField("event_id", bidder?.meta?.event_id || "");
      form.setField("emd_amount", bidder?.meta?.emd_amount || "");

      // Load EMD paid date from payment_data if available
      if (paymentData?.emd_paid_date) {
        const dateValue =
          typeof paymentData.emd_paid_date === "string"
            ? new Date(paymentData.emd_paid_date)
            : paymentData.emd_paid_date;
        if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
          form.setField("emd_paid_date", dateValue);
        }
      }
    }
  }, [bidder, bidderUuid, onboardingData]);

  const handleSubmit = async () => {
    form.setField("emd_proof_uuid", kycFiles);
    console.log("handleSubmit-----", form.validate(), form.errors);
    if (form.validate()) {
      setLoading(true);
      const data: any = await getBidderByUuid(bidderUuid);
      const res5: any = await updateBidderForEvent({
        uuid: bidderUuid,
        meta: {
          ...data?.meta,
          steps_status: {
            ...data?.meta?.steps_status,
            [outerNextStep === null ? "bid_details" : outerNextStep]:
              "completed",
          },
        },
        application_form_data: {
          ...data?.application_form_data,
          form_data: {
            ...data?.application_form_data?.form_data,
            ...form?.value,
          },
          status: "completed",
          // Clear rejection-related fields
          reject_remarks: null,
        },
        payment_data: {
          ...data?.payment_data,
          payment_mode: form.getField("payment_mode"),
          approver_name: "Na",
          emd_paid_date: form.getField("emd_paid_date"),
          emd_paid_from: form.getField("emd_paid_from"),
          approver_email: "Na",
          emd_proof_uuid: form.getField("emd_proof_uuid"),
          transaction_id: form.getField("transaction_id"),
          approval_status: "approved",
          approver_mobile: "Na",
          form_submission: "submitted",
          transaction_amount: form.getField("transaction_amount"),
          // Clear rejection-related fields
          reject_remarks: null,
        },
        verification_step: outerNextStep || "ar_declaration",
        status: "pending",
      });
      // setLoadingText(
      //   "GST details fetched. Redirecting to the next step. Please wait..."
      // );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setActiveStep(activeStep + 1);
      await init();
      setLoading(false);
    }
  };

  return (
    <div>
      {bidder?.application_form_data?.status === "completed" ? (
        <div className="text-sm">This step is completed.</div>
      ) : (
        <div>
          {(bidder?.application_form_data?.status === "rejected" ||
            bidder?.payment_data?.approval_status === "rejected") && (
            <div className="mb-6">
              <div className="border-[1px] border-red-300 bg-red-50 px-4 py-3 rounded-md">
                <div className="flex gap-2 md:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <ExclamationIcon className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base font-semibold text-red-700 mb-2">
                      Application Rejected
                    </div>
                    <div className="text-xs md:text-sm text-red-600 mb-2">
                      <span className="font-semibold">Rejection Remarks:</span>{" "}
                      {bidder?.application_form_data?.reject_remarks ||
                        bidder?.payment_data?.reject_remarks ||
                        "No rejection remarks provided"}
                    </div>
                    <div className="text-xs md:text-sm text-red-700 leading-relaxed">
                      Please review the remarks above and update your bid
                      application details accordingly.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!hideHeader && (
            <h4 className="font-bold text-lg font-openSans">
              {stepsTitles?.[activeStep]?.label}
            </h4>
          )}

          <div className="text-xs md:text-sm text-brand-textGrey mt-2 md:mt-4 mb-4 md:mb-8">
            <strong className="text-brand-textBlack">Property:</strong>{" "}
            {bidder?.meta?.event_name}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="email">Email*</Label>
              <Input
                disabled={true}
                placeholder="Email*"
                value={form.getField("email")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("email") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("email")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="mobile">Mobile*</Label>
              <Input
                disabled={true}
                type="number"
                placeholder={"Mobile*"}
                value={form.getField("mobile")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("mobile") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("mobile")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="pan">PAN Number*</Label>
              <Input
                disabled={true}
                placeholder={"PAN Number*"}
                value={form.getField("pan")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("pan") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("pan")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="name">
                Name*
              </Label>
              <Input
                disabled={true}
                placeholder="Name*"
                value={form.getField("name")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("name") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("name")?.[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="py-8">Bid Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label className="text-brand-bgBlack" htmlFor="event_id">
                Listing Id*
              </Label>
              <Input
                disabled={true}
                placeholder="Listing Id*"
                value={form.getField("event_id")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("event_id") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("event_id")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="emd_amount">
                EMD Amount*
              </Label>
              <Input
                disabled={true}
                placeholder={"EMD Amount*"}
                value={form.getField("emd_amount")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("emd_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_amount")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label
                className="text-brand-bgBlack"
                htmlFor="transaction_amount"
              >
                Transaction Amount*
              </Label>
              <Input
                // disabled={true}
                type="number"
                placeholder={"Transaction Amount*"}
                value={form.getField("transaction_amount")}
                onChange={(e: any) => {
                  const rawValue = e?.target?.value;
                  if (rawValue === "") {
                    form.setField("transaction_amount", "");
                  } else {
                    const numericValue = Number(rawValue);
                    const value = isNaN(numericValue)
                      ? ""
                      : Math.abs(numericValue);
                    form.setField("transaction_amount", value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("transaction_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("transaction_amount")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="bid_amount">Bid Amount*</Label>
              <Input
                type="number"
                placeholder="Bid Amount*"
                value={form.getField("bid_amount")}
                onChange={(e: any) => {
                  const rawValue = e?.target?.value;
                  if (rawValue === "") {
                    form.setField("bid_amount", "");
                  } else {
                    const numericValue = Number(rawValue);
                    const value = isNaN(numericValue)
                      ? ""
                      : Math.abs(numericValue);
                    form.setField("bid_amount", value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("bid_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("bid_amount")?.[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="py-8">EMD Payment Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div className="col-span-1 md:col-span-2">
              <CustomMultipleFileUploader
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["image/png", "image/jpeg"]}
                property_uuid={bidderUuid}
                owner_service_type="emd-payment-slip"
                data={kycFiles}
                defaultText="Please upload proof of your EMD payment"
              />
              {form.errors.get("emd_proof_uuid") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_proof_uuid")[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="payment_mode">Payment Mode*</Label>
              <Dropdown
                dataSource={() => {
                  return Promise.resolve(emdPaymentMode);
                }}
                defaultSelected={
                  form.getField("payment_mode") !== ""
                    ? form.getField("payment_mode")
                    : null
                }
                selectIdentifier={true}
                onChange={async (value: any) => {
                  form.setField("payment_mode", value);
                }}
                valueKey="value"
                placeholder="Select Payment Mode*"
                styles={{
                  hideDot: true,
                  dropdownItemsClass: "text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightPink",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  outerContainer:
                    "border-[1px] border-brand-borderLightGray rounded-sm text-xs sm:text-sm leading-normal font-sans text-brand-textGrey px-2 sm:px-4 h-[38px]",
                }}
              />
              {form.errors.get("payment_mode") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("payment_mode")[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="transaction_id"> Transaction Id*</Label>
              <Input
                placeholder={"Transaction Id*"}
                value={form.getField("transaction_id")}
                onChange={(e: any) =>
                  form.setField("transaction_id", e?.target?.value)
                }
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("transaction_id") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("transaction_id")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="emd_paid_date">EMD paid Date*</Label>
              <DateCard form={form} />
              {form.errors.get("emd_paid_date") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_paid_date")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="emd_paid_from">From Bank*</Label>
              <Input
                placeholder="From Bank*"
                type="text"
                value={form.getField("emd_paid_from")}
                onChange={(e: any) => {
                  const textOnly = e.target.value.replace(/[0-9]/g, "");
                  form.setField("emd_paid_from", textOnly);
                  // form.setField("emd_paid_from", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("emd_paid_from") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_paid_from")?.[0]}
                </p>
              ) : null}
            </div>
            {/* <div>
              <Dropdown
                dataSource={() => {
                  return Promise.resolve(types);
                }}
                defaultSelected={
                  form.getField("relationship") !== ""
                    ? form.getField("relationship")
                    : null
                }
                selectIdentifier={true}
                onChange={async (value: any) => {
                  form.setField("relationship", value);
                }}
                valueKey="value"
                placeholder="Relationship with Seller/E-Auction Vendor*"
                styles={{
                  hideDot: true,
                  dropdownItemsClass: "text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightPink",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  outerContainer:
                    "border-[1px] border-brand-borderLightGray rounded-sm text-xs sm:text-sm leading-normal font-sans text-brand-textGrey px-2 sm:px-4 h-[38px]",
                }}
              />
              {form.errors.get("relationship") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("relationship")[0]}
                </p>
              ) : null}
            </div> */}
          </div>
          <div className="mt-8 mb-10 md:mb-0 flex justify-center">
            <Button
              disabled={loading}
              className="w-full max-w-xl h-10 text-[18px]"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
      {loading && <LoadingModal />}
    </div>
  );
}
interface DateCardProps {
  form: any;
}
const DateCard = ({ form }: DateCardProps) => {
  // const { searchForm }: any = useContext(Context);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);

  // Load existing date from form on mount
  useEffect(() => {
    const emdPaidDate = form.getField("emd_paid_date");
    if (emdPaidDate) {
      const dateValue =
        typeof emdPaidDate === "string" ? new Date(emdPaidDate) : emdPaidDate;
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        setStartDate(dateValue);
      }
    }
  }, [form]);

  const formatDate = (date: Date | undefined): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear().toString();
    return `${day}/${month}/${year}`;
  };

  const handleDateSelect = (date: Date | undefined) => {
    setStartDate(date);
    form.setField("emd_paid_date", date || "");
    setOpen(false);
  };

  return (
    <div className="">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="flex gap-2 items-center w-full justify-between text-sm font-openSans text-brand-textLightGrey"
          >
            {startDate ? (
              formatDate(startDate)
            ) : (
              <span>Select EMD paid Date</span>
            )}
            <CalendarDotsIcon width={16} height={16} />
          </Button>
        </DialogTrigger>
        <DialogContent removeIcon={true} className="max-w-[340px] rounded-lg">
          <DialogTitle className="hidden"> </DialogTitle>
          <DialogDescription className="hidden"></DialogDescription>
          <div className="font-openSans text-lg font-semibold text-brand-textLightGrey px-3">
            Select EMD paid Date
          </div>
          <Calendar
            mode="single"
            selected={startDate}
            onSelect={handleDateSelect}
            fromDate={new Date(new Date().setMonth(new Date().getMonth() - 2))}
            toDate={new Date()}
            initialFocus
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface LoadingModalProps {
  loadingText?: string;
}

const LoadingModal = ({ loadingText }: LoadingModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className=" text-white flex flex-col relative">
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
          <div className="mt-2 text-sm font-openSans text-center">
            {loadingText || "Saving Details. Please wait"}
          </div>
        </div>
      </div>
    </div>
  );
};
